import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus, Search, FolderKanban, Calendar, Clock, AlertTriangle,
  Edit, Archive, Users, Target, Activity, MessageSquare,
  Paperclip, ChevronRight, Send, X, ArrowLeft, RefreshCw, CheckCircle2, Trash2,
  Brain, Sparkles, FileText, TrendingUp, Copy, Building2, ArrowRight, ChevronDown, AlertCircle, Loader2,
  Coins, DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { projectAPI, milestoneAPI, taskAPI, commentAPI, userAPI, aiAPI } from '../api';
import { Modal, Avatar, StatusBadge, ProgressBar, Loader, EmptyState, Pagination } from '../App.core';
import { formatDate, formatRelative, ROLE_LABELS, getProgressColor, isOverdue } from '../utils/helpers';

const formatContractValue = (val, currency = 'AED') => {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(val);
  if (isNaN(num)) return null;
  return `${num.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency}`;
};

// =============================================
// PROJECT LIST PAGE
// =============================================

export const ProjectListPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ status: '', priority: '', search: '', page: 1 });
  const [showCreate, setShowCreate] = useState(false);
  const { canCreateProject } = useAuth();
  const navigate = useNavigate();

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 10 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await projectAPI.list(params);
      setProjects(data.projects);
      setPagination(data.pagination);
    } catch (err) { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  return (
    <>
      {/* Header matching Dashboard Design exactly */}
      <div className="dashboard-welcome-banner">
        <div>
          <h2 className="dashboard-welcome-title">Projects</h2>
          <div className="dashboard-welcome-subtitle">
            <span>Manage active construction sites, programmes, and project deliveries.</span>
          </div>
        </div>
        {canCreateProject() && (
          <div className="dashboard-welcome-actions">
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <Plus size={15} />
              <span>New Project</span>
            </button>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search by name, site code, or client..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            >
              <option value="">All Statuses</option>
              <option value="preconstruction">Pre-Construction</option>
              <option value="mobilization">Mobilization</option>
              <option value="active">Active Construction</option>
              <option value="on_hold">On Hold</option>
              <option value="substantially_complete">Substantially Complete</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.priority}
              onChange={e => setFilters(f => ({ ...f, priority: e.target.value, page: 1 }))}
            >
              <option value="">All Priorities</option>
              <option value="routine">Routine</option>
              <option value="standard">Standard</option>
              <option value="urgent">Urgent</option>
              <option value="safety_critical">Safety Critical</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {loading ? <Loader /> : projects.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No projects found" message="Create your first project to get started"
          action={canCreateProject() && <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Create Project</button>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {projects.map(p => (
            <div
              key={p.id}
              className="project-full-card"
              onClick={() => navigate(`/projects/${p.id}`)}
            >
              <div className="project-full-avatar">
                <Building2 size={20} />
              </div>

              <div className="project-full-info">
                <div className="project-full-title-row">
                  <div style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="project-full-name">{p.name}</span>
                    <span className="project-full-code">{p.project_code}</span>
                  </div>
                  <div style={{ width: 145, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
                    <StatusBadge status={p.status} />
                  </div>
                  <div style={{ width: 110, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
                    <StatusBadge status={p.priority} type="priority" />
                  </div>
                </div>
                <div className="project-full-meta">
                  <span className="project-full-meta-item">
                    <Calendar size={13} style={{ color: 'var(--px-text-muted)' }} />
                    <span>{formatDate(p.start_date)} — {formatDate(p.target_end_date)}</span>
                  </span>
                  {p.contract_value && (
                    <span className="project-full-meta-item" style={{ color: '#047857', fontWeight: 600 }}>
                      <Coins size={13} style={{ color: '#059669' }} />
                      <span>{formatContractValue(p.contract_value, p.settings?.currency)}</span>
                    </span>
                  )}
                  {p.owner && (
                    <span className="project-full-meta-item">
                      <Users size={13} style={{ color: 'var(--px-text-muted)' }} />
                      <span>{p.owner.first_name} {p.owner.last_name}</span>
                    </span>
                  )}
                </div>
              </div>

              <div className="project-full-progress-section">
                <div className="project-full-progress-header">
                  <span className="project-full-progress-label">Progress</span>
                  <span className="project-full-progress-val">{p.progress || 0}%</span>
                </div>
                <div className="project-progress-bar">
                  <div
                    className="project-progress-bar-fill"
                    style={{ width: `${Math.min(p.progress || 0, 100)}%` }}
                  />
                </div>
                {p.task_stats && (
                  <div className="project-full-task-count">
                    <span>{p.task_stats.completed}/{p.task_stats.total} activities</span>
                    {p.task_stats.overdue > 0 && (
                      <span style={{ color: 'var(--px-danger)', fontWeight: 600 }}>• {p.task_stats.overdue} overdue</span>
                    )}
                  </div>
                )}
              </div>

              <div className="project-full-actions">
                <div className="avatar-stack">
                  {(p.members || []).slice(0, 3).map(m => (
                    <Avatar key={m.user?.id} firstName={m.user?.first_name} lastName={m.user?.last_name} size="avatar-sm" />
                  ))}
                  {(p.members || []).length > 3 && (
                    <div className="avatar avatar-sm" style={{ background: 'var(--px-surface-active)', color: 'var(--px-text-secondary)', fontSize: '0.6rem' }}>
                      +{p.members.length - 3}
                    </div>
                  )}
                </div>
                <div style={{ color: 'var(--px-text-muted)', display: 'flex', alignItems: 'center' }}>
                  <ArrowRight size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (pagination.total > 10 || pagination.total_pages > 1) && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        />
      )}

      {showCreate && <ProjectFormModal onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); loadProjects(); }} />}
    </>
  );
};

// =============================================
// PROJECT CREATE/EDIT MODAL
// =============================================

const ProjectFormModal = ({ project, onClose, onSuccess }) => {
  const { user } = useAuth();
  const currency = project?.settings?.currency || 'AED';
  const [form, setForm] = useState({
    name: project?.name || '', project_code: project?.project_code || '',
    description: project?.description || '', owner_id: project?.owner_id || user?.id || '',
    start_date: project?.start_date || new Date().toISOString().split('T')[0],
    target_end_date: project?.target_end_date || '',
    priority: project?.priority || 'standard', status: project?.status || 'preconstruction',
    contract_value: project?.contract_value !== undefined && project?.contract_value !== null ? project.contract_value : '',
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    userAPI.list({ limit: 100, status: 'active' }).then(({ data }) => setUsers(data.users)).catch(() => { });
  }, []);

  const todayStr = new Date().toISOString().split('T')[0];

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Project name is required.';
    if (!form.project_code.trim()) errors.project_code = 'Project code is required.';

    if (!form.start_date) {
      errors.start_date = 'Start date is required.';
    } else if (!project && form.start_date < todayStr) {
      errors.start_date = 'Start date cannot be earlier than today.';
    }

    if (!form.target_end_date) {
      errors.target_end_date = 'Target end date is required.';
    } else if (!project && form.target_end_date < todayStr) {
      errors.target_end_date = 'Target end date cannot be earlier than today.';
    } else if (form.start_date && form.target_end_date < form.start_date) {
      errors.target_end_date = 'Target end date cannot be earlier than start date.';
    }

    if (!form.owner_id) errors.owner_id = 'Please select a project lead.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      if (project) await projectAPI.update(project.id, form);
      else await projectAPI.create(form);
      toast.success(project ? 'Project updated' : 'Project created');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to save project. Please verify all details.';
      setServerError(msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => {
    setServerError('');
    setFieldErrors(prev => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Building2 size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              {project ? 'Edit Project' : 'Create New Project'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              {project ? 'Update programme timeline and construction delivery parameters' : 'Register a new construction programme, site code, and schedule parameters'}
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 120 }}>
            {saving ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
          </button>
        </div>
      }
    >

      {serverError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 8,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            fontSize: '0.84rem',
            fontWeight: 500,
            marginBottom: 18
          }}
        >
          <AlertCircle size={17} style={{ flexShrink: 0, color: '#DC2626' }} />
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Project Name <span className="required">*</span></label>
            <input
              className={`form-input ${fieldErrors.name ? 'has-error' : ''}`}
              value={form.name}
              onChange={set('name')}
              placeholder="e.g. Al Noor Mixed-Use Tower"
            />
            {fieldErrors.name && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.name}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Project Code <span className="required">*</span></label>
            <input
              className={`form-input ${fieldErrors.project_code ? 'has-error' : ''}`}
              value={form.project_code}
              onChange={set('project_code')}
              placeholder="e.g. ANT-001"
              disabled={!!project}
              style={{ fontFamily: 'var(--px-font-mono)', letterSpacing: '0.5px' }}
            />
            {fieldErrors.project_code && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.project_code}</div>}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Project Scope & Description</label>
          <textarea
            className="form-textarea"
            value={form.description}
            onChange={set('description')}
            placeholder="Brief scope of works, programme milestones, or site notes..."
            style={{ minHeight: 75 }}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date <span className="required">*</span></label>
            <input
              className={`form-input ${fieldErrors.start_date ? 'has-error' : ''}`}
              type="date"
              min={!project ? todayStr : undefined}
              value={form.start_date}
              onChange={set('start_date')}
            />
            {fieldErrors.start_date && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.start_date}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Target End Date <span className="required">*</span></label>
            <input
              className={`form-input ${fieldErrors.target_end_date ? 'has-error' : ''}`}
              type="date"
              min={!project ? (form.start_date || todayStr) : (form.start_date || undefined)}
              value={form.target_end_date}
              onChange={set('target_end_date')}
            />
            {fieldErrors.target_end_date && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.target_end_date}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Project Owner / Lead <span className="required">*</span></label>
            <div className={`form-select-wrapper ${fieldErrors.owner_id ? 'has-error' : ''}`}>
              <select className="form-select" value={form.owner_id} onChange={set('owner_id')}>
                <option value="">Select project owner</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({ROLE_LABELS[u.role] || u.role})</option>)}
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
            {fieldErrors.owner_id && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.owner_id}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Priority Level</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.priority} onChange={set('priority')}>
                <option value="routine">Routine</option>
                <option value="standard">Standard</option>
                <option value="urgent">Urgent</option>
                <option value="safety_critical">Safety Critical</option>
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Contract Value ({currency})</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type="number"
                step="any"
                min="0"
                value={form.contract_value}
                onChange={set('contract_value')}
                placeholder="e.g. 185000000"
                style={{ paddingLeft: 36 }}
              />
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--px-text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                {currency}
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Current Construction Status</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.status} onChange={set('status')}>
                <option value="preconstruction">Pre-Construction</option>
                <option value="mobilization">Mobilization</option>
                <option value="active">Active Construction</option>
                <option value="on_hold">On Hold</option>
                <option value="substantially_complete">Substantially Complete</option>
                <option value="completed">Completed</option>
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// =============================================
// PROJECT DETAIL PAGE
// =============================================

export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, canCreateProject } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [comments, setComments] = useState([]);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [newComment, setNewComment] = useState('');

  const [updatingTaskId, setUpdatingTaskId] = useState(null);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await projectAPI.getById(id);
      setProject(data.project);
    } catch (err) { toast.error('Failed to load project'); navigate('/projects'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await taskAPI.list({ project_id: id, limit: 100, sort_by: 'created_at', sort_order: 'DESC' });
      setTasks(data.tasks);
    } catch (err) { }
  }, [id]);

  const loadMilestones = useCallback(async () => {
    try {
      const { data } = await milestoneAPI.list({ project_id: id });
      setMilestones(data.milestones);
    } catch (err) { }
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await commentAPI.list({ project_id: id });
      setComments(data.comments);
    } catch (err) { }
  }, [id]);

  useEffect(() => { loadProject(); loadTasks(); loadMilestones(); loadComments(); }, [loadProject, loadTasks, loadMilestones, loadComments]);

  const handleConfirmArchive = async () => {
    setArchiving(true);
    try {
      await projectAPI.archive(id);
      toast.success('Project archived');
      navigate('/projects');
    } catch (err) {
      toast.error('Failed to archive project');
    } finally {
      setArchiving(false);
      setShowArchiveModal(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await commentAPI.create({ content: newComment, project_id: id });
      setNewComment('');
      loadComments();
      toast.success('Comment added');
    } catch (err) { toast.error('Failed to add comment'); }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    // 1. Immediately update UI state optimistically so the dropdown reflects newStatus right away
    const prevTasks = [...tasks];
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, status: newStatus } : t)));
    setUpdatingTaskId(taskId);

    try {
      await taskAPI.update(taskId, { status: newStatus });
      toast.success('Status updated');
      // Refresh tasks and project stats in background
      loadTasks();
      loadProject();
    } catch (err) {
      // Revert if request failed
      setTasks(prevTasks);
      toast.error(err.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  if (loading) return <Loader />;
  if (!project) return null;

  const canEdit = hasRole('project_director', 'project_manager') || project.owner_id === user?.id;
  const stats = project.task_stats || {};

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/projects" className="back-nav-link">
          <ArrowLeft size={15} />
          <span>Back to Projects</span>
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
                color: '#0038A7',
                border: '1px solid rgba(0, 56, 167, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Building2 size={24} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.4px', margin: 0 }}>
                  {project.name}
                </h2>
                <span className="project-full-code">{project.project_code}</span>
                <StatusBadge status={project.status} />
                <StatusBadge status={project.priority} type="priority" />
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--px-text-muted)', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Calendar size={13} style={{ color: 'var(--px-text-muted)' }} />
                  {formatDate(project.start_date)} — {formatDate(project.target_end_date)}
                </span>
                {project.contract_value && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#047857', fontWeight: 650 }}>
                    <Coins size={13} style={{ color: '#059669' }} />
                    {formatContractValue(project.contract_value, project.settings?.currency)}
                  </span>
                )}
                {project.owner && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Users size={13} style={{ color: 'var(--px-text-muted)' }} />
                    Lead: {project.owner.first_name} {project.owner.last_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowEditProject(true)}>
                <Edit size={14} /> Edit Project
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => setShowArchiveModal(true)}>
                <Archive size={14} /> Archive
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress & KPI Overview Matching Dashboard Design */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 24 }}>
        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Contract Value</span>
            <div className="kpi-card-icon-pill">
              <Coins size={16} />
            </div>
          </div>
          <div className="kpi-value" style={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
            {project.contract_value ? formatContractValue(project.contract_value, project.settings?.currency) : 'Not Specified'}
          </div>
          <div className="kpi-card-footer">
            <span>Total agreed contract sum</span>
          </div>
        </div>

        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Progress</span>
            <div className="kpi-card-icon-pill">
              <TrendingUp size={16} />
            </div>
          </div>
          <div className="kpi-value">{project.progress || 0}%</div>
          <div style={{ marginTop: 6, marginBottom: 4 }}>
            <div className="project-progress-bar">
              <div
                className="project-progress-bar-fill"
                style={{ width: `${Math.min(project.progress || 0, 100)}%` }}
              />
            </div>
          </div>
          <div className="kpi-card-footer">
            <span>Overall completion index</span>
          </div>
        </div>

        <div className="kpi-card kpi-cyan">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Activities</span>
            <div className="kpi-card-icon-pill">
              <Activity size={16} />
            </div>
          </div>
          <div className="kpi-value">{stats.total || 0}</div>
          <div className="kpi-card-footer">
            <span>Scheduled work items</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Completed</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{stats.completed || 0}</div>
          <div className="kpi-card-footer">
            <span>Successfully closed out</span>
          </div>
        </div>

        <div className="kpi-card kpi-danger">
          <div className="kpi-card-header">
            <span className="kpi-label">Overdue</span>
            <div className="kpi-card-icon-pill">
              <AlertTriangle size={16} />
            </div>
          </div>
          <div className="kpi-value">{stats.overdue || 0}</div>
          <div className="kpi-card-footer">
            <span>Activities past deadline</span>
          </div>
        </div>

        <div className="kpi-card kpi-muted">
          <div className="kpi-card-header">
            <span className="kpi-label">Team Members</span>
            <div className="kpi-card-icon-pill">
              <Users size={16} />
            </div>
          </div>
          <div className="kpi-value">{(project.members || []).length}</div>
          <div className="kpi-card-footer">
            <span>Assigned site workforce</span>
          </div>
        </div>
      </div>

      {/* Modern Tabs */}
      <div className="tabs">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'tasks', label: `Activities (${tasks.length})` },
          { key: 'milestones', label: `Milestones (${milestones.length})` },
          { key: 'activity', label: 'Discussion' },
          { key: 'team', label: `Team (${(project.members || []).length})` },
          { key: 'ai', label: 'AI Insights', isAi: true }
        ].map(tab => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            style={tab.isAi && activeTab === tab.key ? { color: '#00AFC0', borderBottomColor: '#00AFC0' } : undefined}
          >
            {tab.isAi && <Sparkles size={14} style={{ marginRight: 6, verticalAlign: -2, color: activeTab === tab.key ? '#00AFC0' : '#00AFC0' }} />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Project Overview</div>
                  <div className="card-subtitle">Scope, commercial particulars, and technical delivery</div>
                </div>
              </div>
              <p style={{ fontSize: '0.88rem', color: 'var(--px-text-secondary)', lineHeight: 1.7, margin: '0 0 16px 0' }}>
                {project.description || 'No detailed scope description provided.'}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid var(--px-border)' }}>
                <div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', textTransform: 'uppercase', fontWeight: 650, letterSpacing: '0.5px' }}>Contract Value</div>
                  <div style={{ fontSize: '1rem', fontWeight: 750, color: '#047857', marginTop: 3 }}>
                    {project.contract_value ? formatContractValue(project.contract_value, project.settings?.currency) : '—'}
                  </div>
                </div>
                {project.site_location && (
                  <div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', textTransform: 'uppercase', fontWeight: 650, letterSpacing: '0.5px' }}>Site Location</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0B1220', marginTop: 3 }}>
                      {project.site_location}
                    </div>
                  </div>
                )}
                {project.client_name && (
                  <div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', textTransform: 'uppercase', fontWeight: 650, letterSpacing: '0.5px' }}>Client</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0B1220', marginTop: 3 }}>
                      {project.client_name}
                    </div>
                  </div>
                )}
                {project.consultant_name && (
                  <div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', textTransform: 'uppercase', fontWeight: 650, letterSpacing: '0.5px' }}>Consultant</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0B1220', marginTop: 3 }}>
                      {project.consultant_name}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Recent Activities</div>
                  <div className="card-subtitle">Key works happening on this site</div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setShowCreateTask(true)}>
                  <Plus size={14} /> Add Activity
                </button>
              </div>
              {tasks.length === 0 ? (
                <EmptyState title="No activities yet" message="Add the first activity to track work progress" />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                  {tasks.slice(0, 8).map(t => {
                    const overdue = t.due_date && isOverdue(t.due_date);
                    return (
                      <div
                        key={t.id}
                        className="task-row-card"
                        style={{ cursor: 'default' }}
                      >
                        <div className="task-status-dropdown-wrap">
                          <select
                            value={t.status}
                            onChange={e => handleTaskStatusChange(t.id, e.target.value)}
                            disabled={updatingTaskId === t.id}
                            className={`task-status-dropdown status-${t.status}`}
                          >
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="on_hold">On Hold</option>
                            <option value="inspection">Inspection</option>
                            <option value="rework">Rework</option>
                            <option value="completed">Completed</option>
                          </select>
                          {updatingTaskId === t.id ? (
                            <Loader2 size={12} className="task-status-spinner" />
                          ) : (
                            <ChevronDown size={12} className="task-status-chevron" />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0B1220' }}>{t.title}</div>
                          {t.milestone && (
                            <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', marginTop: 2 }}>
                              Milestone: {t.milestone.name}
                            </div>
                          )}
                        </div>
                        <StatusBadge status={t.priority} type="priority" />
                        {t.assignee && (
                          <div title={`${t.assignee.first_name} ${t.assignee.last_name}`}>
                            <Avatar firstName={t.assignee.first_name} lastName={t.assignee.last_name} size="avatar-sm" />
                          </div>
                        )}
                        {t.due_date && (
                          <span className={`task-due-badge ${overdue && t.status !== 'completed' ? 'overdue' : ''}`}>
                            <Calendar size={12} />
                            <span>{formatDate(t.due_date)}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Key Milestones</div>
                  <div className="card-subtitle">Major programme deliverables</div>
                </div>
                {canEdit && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateMilestone(true)}>
                    <Plus size={14} /> Add
                  </button>
                )}
              </div>
              {milestones.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--px-text-muted)', margin: 0 }}>No milestones established yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                  {milestones.map(m => (
                    <div
                      key={m.id}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--px-border)',
                        background: 'var(--px-surface-hover)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 620, fontSize: '0.88rem', color: '#0B1220' }}>{m.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <StatusBadge status={m.status} />
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0B1220' }}>{m.progress || 0}%</span>
                        </div>
                      </div>
                      <div className="project-progress-bar" style={{ height: 5 }}>
                        <div
                          className="project-progress-bar-fill"
                          style={{ width: `${Math.min(m.progress || 0, 100)}%` }}
                        />
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                        <span>{m.task_count?.completed || 0}/{m.task_count?.total || 0} activities</span>
                        {m.due_date && <span>Due {formatDate(m.due_date)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">All Programme Activities ({tasks.length})</div>
              <div className="card-subtitle">Complete schedule breakdown for this project</div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreateTask(true)}>
              <Plus size={14} /> Add Activity
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Title</th>
                  <th>Priority</th>
                  <th>Assignee</th>
                  <th>Milestone</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => {
                  const overdue = t.due_date && isOverdue(t.due_date);
                  return (
                    <tr key={t.id}>
                      <td style={{ width: 160 }}>
                        <div className="task-status-dropdown-wrap">
                          <select
                            value={t.status}
                            onChange={e => handleTaskStatusChange(t.id, e.target.value)}
                            disabled={updatingTaskId === t.id}
                            className={`task-status-dropdown status-${t.status}`}
                          >
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="on_hold">On Hold</option>
                            <option value="inspection">Inspection</option>
                            <option value="rework">Rework</option>
                            <option value="completed">Completed</option>
                          </select>
                          {updatingTaskId === t.id ? (
                            <Loader2 size={12} className="task-status-spinner" />
                          ) : (
                            <ChevronDown size={12} className="task-status-chevron" />
                          )}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600, color: '#0B1220' }}>{t.title}</td>
                      <td><StatusBadge status={t.priority} type="priority" /></td>
                      <td>
                        {t.assignee ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar firstName={t.assignee.first_name} lastName={t.assignee.last_name} size="avatar-sm" />
                            <span style={{ fontSize: '0.84rem', fontWeight: 500 }}>{t.assignee.first_name} {t.assignee.last_name}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--px-text-muted)' }}>Unassigned</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.84rem', color: 'var(--px-text-secondary)' }}>{t.milestone?.name || '—'}</td>
                      <td>
                        {t.due_date ? (
                          <span className={`task-due-badge ${overdue && t.status !== 'completed' ? 'overdue' : ''}`}>
                            <Calendar size={12} />
                            <span>{formatDate(t.due_date)}</span>
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'milestones' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Project Milestones ({milestones.length})</div>
              <div className="card-subtitle">Major programme deliverables and technical stages</div>
            </div>
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreateMilestone(true)}>
                <Plus size={14} /> Add Milestone
              </button>
            )}
          </div>
          {milestones.length === 0 ? (
            <div style={{ padding: '20px 0' }}>
              <EmptyState title="No milestones" message="Establish major delivery milestones to track programme stages" />
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 14, marginTop: 4 }}>
              {milestones.map(m => (
                <div key={m.id} className="project-full-card" style={{ cursor: 'default' }}>
                  <div className="project-full-avatar">
                    <Target size={22} />
                  </div>
                  <div className="project-full-info">
                    <div className="project-full-title-row">
                      <span className="project-full-name">{m.name}</span>
                      <StatusBadge status={m.status} />
                    </div>
                    {m.description && (
                      <p style={{ fontSize: '0.84rem', color: 'var(--px-text-secondary)', margin: '4px 0 8px 0' }}>
                        {m.description}
                      </p>
                    )}
                    <div className="project-full-meta">
                      {m.due_date && (
                        <span className="project-full-meta-item">
                          <Calendar size={13} style={{ color: 'var(--px-text-muted)' }} />
                          <span>Target: {formatDate(m.due_date)}</span>
                        </span>
                      )}
                      <span className="project-full-meta-item">
                        <span>{m.task_count?.completed || 0} of {m.task_count?.total || 0} tasks completed</span>
                      </span>
                    </div>
                  </div>
                  <div className="project-full-progress-section" style={{ width: 160 }}>
                    <div className="project-full-progress-header">
                      <span className="project-full-progress-label">Milestone Progress</span>
                      <span className="project-full-progress-val">{m.progress || 0}%</span>
                    </div>
                    <div className="project-progress-bar">
                      <div
                        className="project-progress-bar-fill"
                        style={{ width: `${Math.min(m.progress || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Project Discussion</div>
              <div className="card-subtitle">Real-time team logs and programme updates</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <Avatar firstName={user?.first_name} lastName={user?.last_name} />
            <div style={{ flex: 1 }}>
              <textarea
                className="form-textarea"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Post a site update or discussion note..."
                style={{ minHeight: 70, marginBottom: 10 }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleAddComment} disabled={!newComment.trim()}>
                <Send size={14} /> Post Update
              </button>
            </div>
          </div>
          {comments.length === 0 ? (
            <p style={{ color: 'var(--px-text-muted)', fontSize: '0.87rem', textAlign: 'center', padding: '24px 0' }}>
              No comments posted yet. Start the conversation!
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {comments.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--px-surface-hover)', border: '1px solid var(--px-border)' }}>
                  <Avatar firstName={c.author?.first_name} lastName={c.author?.last_name} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0B1220' }}>
                        {c.author?.first_name} {c.author?.last_name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--px-text-muted)' }}>{formatRelative(c.created_at)}</span>
                    </div>
                    <p style={{ fontSize: '0.88rem', lineHeight: 1.6, margin: 0, color: 'var(--px-text)' }}>{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'team' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Site Team Members ({(project.members || []).length})</div>
              <div className="card-subtitle">Assigned personnel and project responsibilities</div>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>System Role</th>
                  <th>Project Role</th>
                </tr>
              </thead>
              <tbody>
                {(project.members || []).map(m => (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar firstName={m.user?.first_name} lastName={m.user?.last_name} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#0B1220' }}>{m.user?.first_name} {m.user?.last_name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)' }}>{m.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="badge badge-active">{ROLE_LABELS[m.user?.role]}</span></td>
                    <td style={{ textTransform: 'capitalize', fontWeight: 550, color: 'var(--px-text-secondary)' }}>{m.role_in_project}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEditProject && <ProjectFormModal project={project} onClose={() => setShowEditProject(false)} onSuccess={() => { setShowEditProject(false); loadProject(); }} />}
      {showCreateTask && <TaskFormModal projectId={id} milestones={milestones} onClose={() => setShowCreateTask(false)} onSuccess={() => { setShowCreateTask(false); loadTasks(); loadProject(); }} />}
      {showCreateMilestone && <MilestoneFormModal projectId={id} onClose={() => setShowCreateMilestone(false)} onSuccess={() => { setShowCreateMilestone(false); loadMilestones(); }} />}

      {/* Archive Confirmation Modal (Matching Sign Out / Log Out Popup) */}
      <Modal
        isOpen={showArchiveModal}
        onClose={() => !archiving && setShowArchiveModal(false)}
        showClose={false}
        size="modal-xs"
        footer={
          <div className="confirm-modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowArchiveModal(false)}
              disabled={archiving}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleConfirmArchive}
              disabled={archiving}
            >
              {archiving ? (
                <>
                  <RefreshCw size={15} className="btn-spinner" />
                  <span>Archiving...</span>
                </>
              ) : (
                <>
                  <Archive size={15} />
                  <span>Archive</span>
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon-wrapper">
            <Archive size={22} />
          </div>
          <div className="confirm-modal-title">Archive this project?</div>
          <p className="confirm-modal-desc">
            This project will be removed from active views and programmes. You can review archived projects in project administration.
          </p>
          <div className="confirm-modal-user-pill" style={{ marginTop: 12 }}>
            <Building2 size={13} style={{ color: '#0038A7' }} />
            <span style={{ fontWeight: 600 }}>{project.name}</span>
            <span style={{ color: 'var(--px-text-muted)' }}>•</span>
            <span style={{ fontFamily: 'var(--px-font-mono)', fontSize: '0.74rem' }}>{project.project_code}</span>
          </div>
        </div>
      </Modal>

      {activeTab === 'ai' && <ProjectAIPanel projectId={id} projectName={project.name} />}
    </>
  );
};

// =============================================
// PROJECT AI INSIGHTS PANEL (Inline in Project Detail)
// =============================================

const renderMd = (text) => {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gim, '<h4 style="font-weight:600;margin:14px 0 6px;font-size:0.93rem;">$1</h4>')
    .replace(/^## (.*$)/gim, '<h3 style="font-weight:650;margin:18px 0 8px;font-size:1.02rem;color:var(--px-text);">$1</h3>')
    .replace(/^# (.*$)/gim, '<h2 style="font-weight:700;margin:20px 0 10px;font-size:1.1rem;">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gim, '<div style="padding-left:14px;margin:2px 0;"><span style="color:var(--px-primary);margin-right:5px;">•</span>$1</div>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
};

const ProjectAIPanel = ({ projectId, projectName }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeAction, setActiveAction] = useState(null);

  const actions = [
    { key: 'summary', label: 'Executive Summary', icon: FileText, color: '#3b82f6', fn: () => aiAPI.projectSummary(projectId) },
    { key: 'risks', label: 'Risk Analysis', icon: AlertTriangle, color: '#ef4444', fn: () => aiAPI.riskAnalysis(projectId) },
    { key: 'status', label: 'Status Report', icon: TrendingUp, color: '#059669', fn: () => aiAPI.statusReport(projectId) },
    { key: 'tasks', label: 'Suggest Tasks', icon: Plus, color: '#00AFC0', fn: () => aiAPI.suggestTasks(projectId) },
    { key: 'workload', label: 'Optimize Workload', icon: Users, color: '#f59e0b', fn: () => aiAPI.optimizeWorkload(projectId) },
    { key: 'timeline', label: 'Timeline Forecast', icon: Clock, color: '#0284c7', fn: () => aiAPI.timelinePrediction(projectId) },
    { key: 'meeting', label: 'Meeting Notes', icon: MessageSquare, color: '#06b6d4', fn: () => aiAPI.meetingNotes(projectId, { meeting_type: 'Daily Standup' }) },
  ];

  const handleAction = async (action) => {
    setLoading(true);
    setActiveAction(action.key);
    setResult(null);
    try {
      const res = await action.fn();
      setResult({ text: res.data.result, label: action.label });
    } catch (err) {
      setResult({ text: `**Error:** ${err.response?.data?.error || 'AI request failed. Ensure GEMINI_API_KEY is set.'}`, label: 'Error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="ai-panel-header">
        <div className="ai-panel-icon-wrap">
          <Sparkles size={22} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="ai-panel-title">AI Intelligence & Analytics</span>
            <span className="ai-assistant-badge">PROPEL AI</span>
          </div>
          <div className="ai-panel-subtitle">
            Instant Gemini-powered risk detection, executive briefs, and predictive timelines for {projectName}
          </div>
        </div>
      </div>

      <div className="ai-action-grid">
        {actions.map(a => {
          const isActive = activeAction === a.key;
          return (
            <button
              key={a.key}
              type="button"
              className={`ai-action-card ${isActive ? 'active' : ''}`}
              onClick={() => handleAction(a)}
              disabled={loading}
            >
              <a.icon size={16} style={{ color: isActive ? '#00AFC0' : a.color, flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.label}</span>
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="card" style={{ textAlign: 'center', padding: '44px 20px', borderRadius: 12 }}>
          <div className="spinner" style={{ margin: '0 auto 14px' }} />
          <p style={{ fontSize: '0.92rem', fontWeight: 650, color: '#0B1220', margin: 0 }}>Analyzing project telemetry...</p>
          <p style={{ fontSize: '0.82rem', color: 'var(--px-text-muted)', marginTop: 4 }}>
            Evaluating schedule risks, milestone deliverables, and activity blockers.
          </p>
        </div>
      )}

      {result && !loading && (
        <div className="ai-result-card">
          <div className="ai-result-header">
            <div className="ai-result-header-title">
              <Sparkles size={16} style={{ color: result.label === 'Error' ? '#ef4444' : '#00AFC0' }} />
              <span>{result.label}</span>
            </div>
            <button
              type="button"
              className="ai-copy-btn"
              onClick={() => {
                if (result?.text) {
                  navigator.clipboard.writeText(result.text);
                  toast.success('Copied to clipboard');
                }
              }}
            >
              <Copy size={13} />
              <span>Copy Report</span>
            </button>
          </div>
          <div
            className="ai-result-body"
            dangerouslySetInnerHTML={{ __html: renderMd(result.text) }}
          />
        </div>
      )}

      {!result && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '52px 20px', color: 'var(--px-text-muted)', borderRadius: 12 }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 12,
              background: 'rgba(0, 175, 192, 0.08)',
              color: '#00AFC0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px'
            }}
          >
            <Sparkles size={26} />
          </div>
          <h4 style={{ fontSize: '0.98rem', fontWeight: 650, color: '#0B1220', margin: '0 0 6px 0' }}>
            Select an AI Assessment Action
          </h4>
          <p style={{ fontSize: '0.84rem', margin: 0, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            Choose an option above to generate an instant executive summary, risk audit, schedule optimization, or timeline forecast.
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================
// TASK CREATE MODAL
// =============================================

const TaskFormModal = ({ projectId, milestones = [], task, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    project_id: projectId || task?.project_id || '',
    milestone_id: task?.milestone_id || '',
    title: task?.title || '',
    description: task?.description || '',
    assignee_id: task?.assignee_id || '',
    priority: task?.priority || 'standard',
    status: task?.status || 'not_started',
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    start_date: task?.start_date ? task.start_date.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    userAPI.list({ limit: 100, status: 'active' })
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => { });
  }, []);

  const todayStr = new Date().toISOString().split('T')[0];

  const validate = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = 'Activity title is required.';

    if (!task && form.start_date && form.start_date < todayStr) {
      errors.start_date = 'Start date cannot be earlier than today.';
    }

    if (!task && form.due_date && form.due_date < todayStr) {
      errors.due_date = 'Due date cannot be earlier than today.';
    } else if (form.start_date && form.due_date && form.due_date < form.start_date) {
      errors.due_date = 'Due date cannot be earlier than start date.';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.milestone_id) delete payload.milestone_id;
      if (!payload.assignee_id) delete payload.assignee_id;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.due_date) delete payload.due_date;

      if (task) await taskAPI.update(task.id, payload);
      else await taskAPI.create(payload);

      toast.success(task ? 'Activity updated' : 'Activity created');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to save activity. Please check all fields.';
      setServerError(msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => {
    setServerError('');
    setFieldErrors(prev => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Activity size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              {task ? 'Edit Activity' : 'Create New Activity'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              {task ? 'Update task deliverables, site assignments, and schedule' : 'Register a new site activity, schedule deadline, and workforce allocation'}
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 120 }}>
            {saving ? 'Saving...' : task ? 'Update Activity' : 'Create Activity'}
          </button>
        </div>
      }
    >

      {serverError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 8,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            fontSize: '0.84rem',
            fontWeight: 500,
            marginBottom: 18
          }}
        >
          <AlertCircle size={17} style={{ flexShrink: 0, color: '#DC2626' }} />
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label className="form-label">Activity Title <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.title ? 'has-error' : ''}`}
            value={form.title}
            onChange={set('title')}
            placeholder="e.g. Substructure excavation & pile head chipping"
          />
          {fieldErrors.title && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.title}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Description & Scope</label>
          <textarea
            className="form-textarea"
            value={form.description}
            onChange={set('description')}
            placeholder="Detailed method statement, task requirements, or inspection notes..."
            style={{ minHeight: 75 }}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Assignee</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.assignee_id} onChange={set('assignee_id')}>
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({ROLE_LABELS[u.role] || u.role})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Associated Milestone</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.milestone_id} onChange={set('milestone_id')}>
                <option value="">No milestone linked</option>
                {milestones.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Priority</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.priority} onChange={set('priority')}>
                <option value="routine">Routine</option>
                <option value="standard">Standard</option>
                <option value="urgent">Urgent</option>
                <option value="safety_critical">Safety Critical</option>
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.status} onChange={set('status')}>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="inspection">Inspection</option>
                <option value="rework">Rework</option>
                <option value="completed">Completed</option>
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input
              className={`form-input ${fieldErrors.start_date ? 'has-error' : ''}`}
              type="date"
              min={!task ? todayStr : undefined}
              value={form.start_date}
              onChange={set('start_date')}
            />
            {fieldErrors.start_date && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.start_date}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input
              className={`form-input ${fieldErrors.due_date ? 'has-error' : ''}`}
              type="date"
              min={!task ? (form.start_date || todayStr) : (form.start_date || undefined)}
              value={form.due_date}
              onChange={set('due_date')}
            />
            {fieldErrors.due_date && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.due_date}</div>}
          </div>
        </div>
      </form>
    </Modal>
  );
};

// =============================================
// MILESTONE CREATE MODAL
// =============================================

const MilestoneFormModal = ({ projectId, onClose, onSuccess }) => {
  const [form, setForm] = useState({ project_id: projectId, name: '', description: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const todayStr = new Date().toISOString().split('T')[0];

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Milestone title is required.';
    if (form.due_date && form.due_date < todayStr) {
      errors.due_date = 'Target delivery date cannot be earlier than today.';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      await milestoneAPI.create(payload);
      toast.success('Milestone created');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to create milestone. Please check your inputs.';
      setServerError(msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => {
    setServerError('');
    setFieldErrors(prev => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Target size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Create New Milestone
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Establish a major programme deliverable, handover gate, or key site achievement
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 140 }}>
            {saving ? 'Creating...' : 'Create Milestone'}
          </button>
        </div>
      }
    >

      {serverError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 8,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            fontSize: '0.84rem',
            fontWeight: 500,
            marginBottom: 18
          }}
        >
          <AlertCircle size={17} style={{ flexShrink: 0, color: '#DC2626' }} />
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label className="form-label">Milestone Title <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.name ? 'has-error' : ''}`}
            value={form.name}
            onChange={set('name')}
            placeholder="e.g. Substructure & Foundation Handover"
          />
          {fieldErrors.name && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.name}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Description & Deliverable Scope</label>
          <textarea
            className="form-textarea"
            value={form.description}
            onChange={set('description')}
            placeholder="Summary of deliverables, inspection criteria, or client acceptance standards..."
            style={{ minHeight: 85 }}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Target Completion Date</label>
          <input
            className={`form-input ${fieldErrors.due_date ? 'has-error' : ''}`}
            type="date"
            min={todayStr}
            value={form.due_date}
            onChange={set('due_date')}
          />
          {fieldErrors.due_date && <div className="form-error"><AlertCircle size={13} /> {fieldErrors.due_date}</div>}
        </div>
      </form>
    </Modal>
  );
};

export { TaskFormModal };
